import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { renderColorBox } from '../../utils/cssStyles';
import { fCurrency } from '../../utils/formatNumber';
import { fDateTime, fMonthDayYear } from '../../utils/formatTime';
import Iconify from '../iconify';
import Image from '../image';
import { cloudinaryThumbnail, cloudinaryFull } from '../../utils/formatCloudinary';
import Lightbox from '../lightbox';
import NotesModal from '../../components/dashboard/rentals/NotesModal';
import OrderTicketsDropdown from '../../components/dashboard/home/OrderTicketsDropdown';

const renderIcon = (value, icon) => (
  <Box>
    <Iconify icon={icon} width={13} height={13} /> {value}
  </Box>
);

export default function RenderImage({ params }) {
  RenderImage.propTypes = {
    params: PropTypes.object,
  };
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = (e) => {
    e.clickedImage = true;
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  return (
    <>
      <Image
        alt="attachment"
        src={
          params.value ||
          cloudinaryThumbnail(
            'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
          )
        }
        sx={{
          cursor: 'pointer',
          maxHeight: 60,
          width: 'auto',
          objectFit: 'contain', 
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={(e) => handleOpenLightbox(e)}
      />
      <Lightbox
        slides={[
          {
            src:
              params.value ||
              cloudinaryFull(
                'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
              ),
          },
        ]}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const digits = phoneNumber.toString().replace(/\D/g, '');
  if (digits.length < 10) return digits;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

//image
const image = {
  field: 'image',
  headerName: 'Image',
  flex: 0.8,
  minWidth: 60,
  maxWidth: 60,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};

const pictures = {
  field: 'pictures',
  headerName: 'Pictures',
  flex: 0.8,
  renderCell: (params) => ( 
    <Box sx={{ display: 'flex', gap: 1 }}>
      {params.value?.map((picture, index) => (
        <Image
          key={index}   
          alt={`attachment-${index}`}
          src={cloudinaryThumbnail(picture)}
          sx={{
            cursor: 'pointer',
            maxHeight: 60,
            maxWidth: 60,
            width: 'auto',
            objectFit: 'contain',
            '&:hover': {        
              opacity: 0.9, 
            },

          }} 
        />  
      ))}   
    </Box>
  ),  
  sortable: false,  
  filterable: false,
};
//description
const description = {
  field: 'description',
  headerName: 'Description',
  minWidth: 120,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
    {params.row.deleted && params.row.closedOn == null ? 
      <Tooltip title={params.row.approvedOn !== null 
        ? "Item removed from catalog — in facility but can no longer be ordered."
        : "Item removed from catalog and cannot be ordered" 
      }> 
        <span style={{ pointerEvents: 'auto' }}>
          {params.value}
        </span>
      </Tooltip> :
      params.value}
    </Box>
  ),
  type: 'customSelect',
};
//category
const category = {
  field: 'department',
  headerName: 'Category',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.department,
  type: 'customSelect',
};
//subcategory
const subcategory = {
  field: 'subcategory',
  headerName: 'Subcategory',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.subcategory,
  type: 'customSelect',
};
//vendor
const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  flex: 1,
  minWidth: 100,
  type: 'customSelect',
};
//itemId
const itemId = {
  field: 'itemId',
  headerName: 'PSR#',
  flex: 0.5,
  minWidth: 70,
  type: 'customSelect',
  renderCell: (params) => `PSR${params.row.itemId}`,
  valueGetter: (params) => `PSR${params.row.itemId}`,
};
//price
const price = {
  field: 'price',
  headerName: 'Price',
  minWidth: 80,
  flex: 1,
  renderCell: (params) => fCurrency(params.value),
};
//name
const name = {
  field: 'name',
  headerName: 'Name',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
};
//emailTo
const emailTo = {
  field: 'emailAddress',
  headerName: 'EmailTo',
  minWidth: 120,
  flex: 1,
  type: 'customSelect'
};
//address
const address = {
  field: 'address',
  headerName: 'Address',
  minWidth: 80,
  flex: 1,
  type: 'customSelect'
};
//city
const city = {
  field: 'city',
  headerName: 'City',
  minWidth: 80,
  flex: 1,
  type: 'customSelect',
};
//state
const state = {
  field: 'state',
  headerName: 'State',
  minWidth: 40,
  flex: 0.3,
  type: 'customSelect',
};
//zip
const zip = {
  field: 'zip',
  headerName: 'Zip',
  minWidth: 70,
  flex: 1,
  type: 'customSelect',
};

const linkedTickets = {
  field: 'ticketIds',
  headerName: 'Ticket #',
  flex: 1,
  minWidth: 80,
  type: 'customSelect',
  renderCell: (params, e) => <OrderTicketsDropdown params={params} e = {e} />,
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.ticketIds ?? [],
};

//phone
const phone = {
  field: 'phone',
  headerName: 'Phone',
  minWidth: 60,
  flex: 1,
  valueGetter: (params) => formatPhoneNumber(params.row.phone),
  getFilterValues: (row) => [formatPhoneNumber(row.phone)],
  renderCell: (params) => (
    <PatternFormat value={params.row.phone} displayType="text" format="(###) ###-####" mask="_" />
  ),
  type: 'customSelect'
};
//orderNumber
const orderNumber = {
  field: 'orderNumber',
  headerName: 'Order Number',
  width: 90,
  flex: 1,
  type: 'customSelect',
  renderCell: (params) => {
    const isRejected = params.row.rejectedBy != null;
    return (
      <Tooltip
        title={
          params.row.approvedBy == null && params.row.rejectedBy 
            ? `Rejected by ${params.row.rejectedBy} On: ${fMonthDayYear(params.row.rejectedOn)}`
            : ''
        }
      >
        <span style={{ color: isRejected ? 'red' : 'inherit' }}>{`${params.value} `}</span>
      </Tooltip>
    );
  },
};
//facilityName
const facilityName = {
  field: 'facilityShortName',
  headerName: 'Facility Name',
  width: 90,
  flex: 1,
  type: 'customSelect',
};
//personFor
const personFor = {
  field: 'personFor',
  headerName: 'Person For',
  width: 90,
  flex: 1,
  type: 'customSelect',
  renderCell: (params) => {
    const formatName = (str) =>
      typeof str === 'string' && str.length > 0
      ? str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
      : str;

    return (
      <Box>
        {params.row.residentDeletedFlag ? (
          <Tooltip title="Deleted Resident.">
            <Box
              sx={{
              textDecoration: params.row.residentDeletedFlag ? 'underline' : 'none',
              textDecorationColor: params.row.residentDeletedFlag ? 'red' : 'inherit',
              color: params.row.residentDeletedFlag ? 'red' : 'inherit',
              }}
            >
              {formatName(params.value)}
            </Box>
          </Tooltip>)
      :
       <Box>
        {formatName(params.value)}
        </Box>
    }
      </Box>
    );
  },
};

//roomNumber
const roomNumber = {
  field: 'room',
  headerName: 'Room Number',
  width: 90,
  flex: 1,
  type: 'customSelect',
};
//department
const department = {
  field: 'department',
  headerName: 'Department',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.department,
  type: 'customSelect',
};
//onBehalfOf
const onBehalfOf = {
  field: 'onBehalfOf',
  headerName: 'On Behalf Of',
  width: 90,
  flex: 1,
  type: 'customSelect',
};
//submittedBy
const submittedBy = {
  field: 'submittedBy',
  headerName: 'Submitted By',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
const submittedOn = {
  field: 'submittedOn',
  headerName: 'Submitted On',
  type: 'customDate',
  minWidth: 100,
  flex: 1.2,
  renderCell: (params) =>
    renderIcon(`${fDateTime(params.row.submittedOn)} EST`, 'material-symbols:calendar-today-outline'),
};
const orderedQty = {
  field: 'orderedQty',
  headerName: 'Qty',
  renderCell: (params) => params.value > 0 && params.value,
  flex: 0.5,
};
const total = {
  field: 'total',
  headerName: 'Total',
  minWidth: 70,
  flex: 0.5,
  renderCell: (params) => fCurrency(params.value),
};
const approvedOn = {
  field: 'approvedOn',
  headerName: 'Approved On',
  type: 'customDate',
  minWidth: 100,
  flex: 1.2,
  renderCell: (params) =>
    params.row.approvedOn ? renderIcon(`${fDateTime(params.row.approvedOn)} EST`, 'material-symbols:calendar-today-outline') : 'Pending Rental',
};
//catalogVendor
const catalogVendor = {
  field: 'vendorName',
  headerName: 'Vendor',
  flex: 1,
  minWidth: 100,
  type: 'customSelect',
};
//vendor item id
const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'Vendor Item Id',
  flex: 1,
  minWidth: 100,
  type: 'customSelect',
};
const psId = {
  field: 'itemId',
  headerName: 'PS Identity',
  flex: 1,
  minWidth: 60,
  renderCell: (params) => params.value ? `${params.value}` : '',
  getFilterValues: (row) => [`PS_R${row.itemId}`],
  type: 'customSelect',
  valueGetter: (params) => `PS_R${params.value}`
};

const id = {
  field: 'id',
  headerName: 'PS Identity',
  flex: 1,
  minWidth: 100,
  renderCell: (params) => params.value ? `${params.value}` : '',
  getFilterValues: (row) => [`PS_R${row.itemId}`],
  type: 'customSelect',
  valueGetter: (params) => `PS_R${params.value}`
};
const pricingType = {
  field: 'pricingTypeName',
  headerName: 'Pricing Type',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
const uomAmount = {
  field: 'uomAmount',
  headerName: 'UOM Amount',
  renderCell: (params) => params.value > 0 && params.value,
  flex: 0.5,
};
const uomType = {
  field: 'uomType',
  headerName: 'UOM Type',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
const capAmount = {
  field: 'capAmount',
  headerName: 'Cap Amount',
  minWidth: 80,
  flex: 1,
  renderCell: (params) => (params.value > 0 ? fCurrency(params.value) : ''),
};
const catalogCategory = {
  field: 'categoryName',
  headerName: 'Category',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.categoryName,
  type: 'customSelect',
};
const catalogSubcategory = {
  field: 'subcategoryName',
  headerName: 'Subcategory',
  minWidth: 110,
  flex: 0.75,
  renderCell: (params) => renderColorBox(params.value, 'secondary'),
  valueGetter: (params) => params.row.subcategoryName,
  type: 'customSelect',
};
//addedBy
const addedBy = {
  field: 'addedBy',
  headerName: 'Added By',
  minWidth: 120,
  flex: 1,
  type: 'customSelect',
};
const dateAdded = {
  field: 'dateAdded',
  headerName: 'Date Added',
  flex: 1,
  renderCell: (params) =>
    params.value ? renderIcon(fMonthDayYear(params.row.dateAdded), 'material-symbols:calendar-today-outline') : '',
  valueGetter: (params) => new Date(params.row.dateAdded),
  type: 'customDate',
};

const accountNumber = {
  field: 'accountNumber',
  headerName: 'Account',
  flex: 1,
  type: 'customSelect',
};
const facility = {
  field: 'facility',
  headerName: 'Facility',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
};
const group = {
  field: 'company',
  headerName: 'Group',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
};
const notes = {
    field: 'note',
    headerName: 'Note',
    minWidth: 60,
    flex: 0.5,
    renderCell: (params) => <NotesModal params={params} />,
  };

const daysOpen = {
  field: 'daysOpen',
  headerName: 'Days Open',
  type: 'number',
  minWidth: 60,
  flex: 0.75,
  renderCell: (params) => {
    if (!params.row.approvedOn) return '-';

    const approvedDate = new Date(params.row.approvedOn);
    const currentDate = new Date();
    const timeDiff = currentDate - approvedDate;
    const dayDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return dayDiff;
  },
};

const RENTAL_ORDER_ITEMS = [
  image,
  description,
  category,
  subcategory,
  itemId,
  price,
  pricingType,
  vendor,
  total
];

const RENTAL_VENDORS = [name, emailTo, address, city, state, zip, phone];

const PENDING_RENTALS = [
  orderNumber,
  facilityName,
  group,
  personFor,
  roomNumber,
  department,
  onBehalfOf,
  submittedBy,
  submittedOn,
  orderedQty,
  total,
  notes,
  linkedTickets

];
const CURRENT_RENTALS = [
  orderNumber,
  facilityName,
  group,
  personFor,
  roomNumber,
  department,
  onBehalfOf,
  daysOpen,
  submittedBy,
  approvedOn,
  orderedQty,
  total,
  notes,
  linkedTickets
];
const PREVIOUS_RENTALS = [
  orderNumber,
  facilityName,
  group,
  personFor,
  roomNumber,
  department,
  onBehalfOf,
  submittedBy,
  submittedOn,
  approvedOn,
  orderedQty,
  total,
  notes,
  linkedTickets
];
const RENTAL_CATALOG = [
  image,
  description,
  catalogCategory,
  catalogSubcategory,
  catalogVendor,
  psId, 
  vendorItemId,
  price,
  pricingType,
  capAmount,
  uomAmount,
  uomType,
  addedBy,
  dateAdded,
];

const RENTAL_UPLOADED_IMAGES = [
  pictures,
  description,
  id, 
  vendorItemId,
]
const ALL_RENTAL_ITEMS = [
  image,
  orderNumber,
  facilityName,
  group,
  description,
  personFor,
  roomNumber,
  category,
  subcategory,
  vendor,
  itemId,
  vendorItemId,
  price,
  pricingType,
  orderedQty,
  total
];
const RENTAL_FACILITY_ACCOUNTS = [facility, group, vendor, accountNumber];
export {
  CURRENT_RENTALS,
  PENDING_RENTALS,
  PREVIOUS_RENTALS,
  RENTAL_CATALOG,
  RENTAL_FACILITY_ACCOUNTS,
  RENTAL_ORDER_ITEMS,
  RENTAL_VENDORS,
  ALL_RENTAL_ITEMS,
  RENTAL_UPLOADED_IMAGES
};
